import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Send, Sunrise, Moon, Copy, Check } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { apiFetch } from '@/api/client';
import * as asuntosApi from '@/api/asuntos.api';
import * as inspectorApi from '@/api/inspector.api';

/**
 * ATHENA — conversación de verdad.
 *
 * Esteban, 20-sep: *"la quiero viva, conectada, que yo pueda hablar con ella
 * como una persona común y corriente, como un agente normal."*
 *
 * Antes eran cuatro botones con respuestas calculadas. Ahora es un chat con el
 * modelo, que ve el estado del día completo.
 *
 * ═══ NO HAY BOTÓN DE ENVIAR A WHATSAPP ═══
 *
 * Athena redacta; mandar lo hace el dueño. Cada respuesta trae "Copiar", y eso
 * es todo lo que la app hace con un texto. La regla no se sostiene con
 * disciplina: no existe la ruta para enviar.
 *
 * ═══ EL HISTORIAL VIVE EN LA PANTALLA ═══
 *
 * No se guarda en la base. Es una charla de trabajo sobre datos que ya están
 * guardados; guardarla crearía una segunda copia de la vida del dueño, con las
 * mismas cosas dichas de otra forma, y después hay que decidir cuál manda.
 */

interface Turno {
  rol: 'usuario' | 'athena';
  texto: string;
}

const SUGERENCIAS = [
  '¿Qué tengo hoy?',
  '¿Qué se me está escapando?',
  '¿Quién me debe?',
  'Prepárame para mi próxima reunión',
  'Redáctame un mensaje para cobrarle a Carlos',
];

function Burbuja({ t, onCopiar }: { t: Turno; onCopiar: (texto: string) => void }) {
  const mio = t.rol === 'usuario';
  const [copiado, setCopiado] = useState(false);

  return (
    <div className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm',
          mio
            ? 'bg-violet-600 text-white'
            : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
        )}
      >
        <p className="whitespace-pre-wrap break-words">{t.texto}</p>
        {/* "Copiar" y nada más. Es todo lo que la app hace con un texto que
            Athena redacta: mandarlo es del dueño. */}
        {!mio && (
          <button
            onClick={() => {
              onCopiar(t.texto);
              setCopiado(true);
              setTimeout(() => setCopiado(false), 1500);
            }}
            className="mt-1.5 flex min-h-[32px] items-center gap-1 text-xs text-slate-500 dark:text-slate-400"
          >
            {copiado ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copiado ? 'Copiado' : 'Copiar'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function Athena() {
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [texto, setTexto] = useState('');
  const fondo = useRef<HTMLDivElement | null>(null);
  const toast = useToast();
  const navegar = useNavigate();

  const asuntos = useQuery({
    queryKey: ['asuntos', 'athena'],
    queryFn: () => asuntosApi.getAsuntos({ limite: 300 }),
  });
  const senales = useQuery({
    queryKey: ['inspector', 'senales'],
    queryFn: inspectorApi.getSenales,
  });

  const preguntar = useMutation({
    mutationFn: (pregunta: string) =>
      apiFetch<{ texto: string; noPudeVer: string[] }>('/api/athena/preguntar', {
        method: 'POST',
        body: { pregunta, historial: turnos.slice(-12) },
      }),
    onSuccess: (r) => setTurnos((t) => [...t, { rol: 'athena', texto: r.texto }]),
    onError: (e: Error) => {
      toast.error(e.message);
      // El turno del usuario se queda: borrarlo le haría reescribir la
      // pregunta por un fallo que no fue suyo.
      setTurnos((t) => [...t, { rol: 'athena', texto: 'No pude responder. Vuelve a intentar.' }]);
    },
  });

  useEffect(() => {
    fondo.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turnos, preguntar.isPending]);

  const enviar = (pregunta: string) => {
    const limpia = pregunta.trim();
    if (!limpia || preguntar.isPending) return;
    setTurnos((t) => [...t, { rol: 'usuario', texto: limpia }]);
    setTexto('');
    preguntar.mutate(limpia);
  };

  const lista = asuntos.data?.asuntos ?? [];
  const vencidos = lista.filter((a) => a.vencido).length;
  const cuantasSenales = senales.data?.senales.length ?? 0;
  const ahora = new Date();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-violet-600 text-white">
          <Sparkles className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Athena</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">Tu secretaria de WhatsApp</p>
        </div>
      </div>

      {/* EL BRIEFING, mientras no haya conversación: es lo que ella sabe sin
          que le preguntes. Al primer mensaje se va, para dejarle sitio al hilo. */}
      {turnos.length === 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          <Card>
            <CardBody>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Sunrise className="h-4 w-4" /> Tu mañana
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {asuntos.isPending
                  ? 'Revisando…'
                  : vencidos > 0
                    ? `Tienes ${vencidos} ${vencidos === 1 ? 'cosa vencida' : 'cosas vencidas'} y ${lista.length} abiertas.`
                    : `Tienes ${lista.length} ${lista.length === 1 ? 'cosa abierta' : 'cosas abiertas'}. Nada vencido.`}
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Moon className="h-4 w-4" /> Tu cierre
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {ahora.getHours() < 18
                  ? 'El cierre del día llega a las 9:00 p. m.'
                  : `${cuantasSenales} ${cuantasSenales === 1 ? 'cosa se te escapó' : 'cosas se te escaparon'} hoy.`}
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      <Card>
        <CardBody className="flex flex-col gap-3">
          <div className="max-h-[52vh] min-h-[160px] space-y-3 overflow-y-auto">
            {turnos.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">
                Pregúntame lo que quieras sobre tu día.
              </p>
            ) : (
              turnos.map((t, i) => (
                <Burbuja
                  key={i}
                  t={t}
                  onCopiar={(x) => {
                    void navigator.clipboard.writeText(x);
                    toast.success('Copiado');
                  }}
                />
              ))
            )}
            {preguntar.isPending && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-slate-100 px-3.5 py-2.5 dark:bg-slate-800">
                  <Spinner />
                </div>
              </div>
            )}
            <div ref={fondo} />
          </div>

          {turnos.length === 0 && (
            <div className="flex flex-wrap gap-1.5">
              {SUGERENCIAS.map((s) => (
                <button
                  key={s}
                  onClick={() => enviar(s)}
                  className="min-h-[36px] rounded-full bg-slate-100 px-3 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && enviar(texto)}
              placeholder="Escríbele a Athena…"
              disabled={preguntar.isPending}
              className="min-h-[44px] flex-1 rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <button
              onClick={() => enviar(texto)}
              disabled={!texto.trim() || preguntar.isPending}
              aria-label="Enviar"
              className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-violet-600 text-white disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </CardBody>
      </Card>

      <p className="px-1 text-xs text-slate-400">
        Athena solo lee y redacta. Nunca manda un WhatsApp: eso lo haces tú.{' '}
        <button
          onClick={() => navegar('/inspector/ajustes')}
          className="text-violet-600 underline-offset-2 hover:underline dark:text-violet-300"
        >
          Ajustes
        </button>
      </p>
    </div>
  );
}
